import { Component, OnInit, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { Campaign, CampaignAdminRequest, CampaignLocationFilter, CampaignsCampaignAdminApiClient, CampaignsCampaignLocationFilterAdminApiClient, CampaignsOutboundEventConfigurationApiClient, LuckyDrawStrategy } from 'src/app/api/api-campaigns.service.generated';
import { MessageDialogModel } from 'src/app/common/message-dialog/message-dialog.component';
import { BannerImage } from '../_/campaign-banner-image/campaign-banner-image.component';
import { CampaignBannerComponent } from '../_/campaign-banner/campaign-banner.component';
import { CampaignBaseEditComponent } from '../_/campaign-base-edit/campaign-base-edit.component';
import { CampaignLocationFiltersComponent } from '../_/campaign-location-filters/campaign-location-filters.component';
import { CampaignMediaComponent } from '../_/campaign-media/campaign-media.component';
import { CampaignOutboundEventsComponent } from '../campaign-outbound-events/campaign-outbound-events.component';
import { LuckyDrawStrategyComponent } from './lucky-draw-strategy/lucky-draw-strategy.component';
import { PERMISSION } from 'src/app/const/permission';
import { HasAnyPermissionDirective } from 'src/app/common/directives/has-any-permission/has-any-permission.directive';
import { PageContainerComponent, BreadcrumbsComponent, ButtonComponent } from 'wecheer-common';
import { AppVersionService } from 'src/app/common/services/app-version.service';
import { ScreenService } from 'src/app/common/services/screen.service';
import { MatTabsModule } from '@angular/material/tabs';
import { CampaignCloneService } from 'src/app/common/services/campaign-clone.service';

@Component({
  selector: 'app-lucky-draw-campaign-edit',
  templateUrl: './lucky-draw-campaign-edit.component.html',
  styleUrls: ['./lucky-draw-campaign-edit.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatTabsModule,
    FlexLayoutModule,
    CampaignBaseEditComponent,
    CampaignBannerComponent,
    CampaignLocationFiltersComponent,
    CampaignMediaComponent,
    CampaignOutboundEventsComponent,
    LuckyDrawStrategyComponent,
    HasAnyPermissionDirective,
    PageContainerComponent,
    BreadcrumbsComponent,
    ButtonComponent
]
})
export class LuckyDrawCampaignEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private campaignLocationFiltersApi = inject(CampaignsCampaignLocationFilterAdminApiClient);
  private outboundEventsApi = inject(CampaignsOutboundEventConfigurationApiClient);
  private campaignCloneService = inject(CampaignCloneService);
  appVersion = inject(AppVersionService);
  screen = inject(ScreenService);


  readonly campaignBase = viewChild<CampaignBaseEditComponent>('campaignBase');
  readonly campaignBanner = viewChild<CampaignBannerComponent>('campaignBanner');
  readonly campaignStrategy = viewChild<LuckyDrawStrategyComponent>('campaignStrategy');
  readonly campaignLocationFilters = viewChild<CampaignLocationFiltersComponent>('campaignLocationFilters');
  readonly campaignOutboundEvents = viewChild<CampaignOutboundEventsComponent>('campaignOutboundEvents');

  editModel: CampaignAdminRequest;

  campaign: Campaign;

  campaignReplay = new BehaviorSubject<Campaign>(undefined);

  campaignId: string;
  advertiserId: string;
  originalCampaignId: string;
  clonedLocationFilters: CampaignLocationFilter;

  isAdding: boolean;
  isCloning: boolean;

  viewMode: 'scheme' | 'locations' | 'json' | 'media' | 'banner' | '' = 'scheme';
  readonly permissions = PERMISSION;


  constructor() {

    this.isAdding = this.route.snapshot.params.campaignId === 'new';
    this.isCloning = this.route.snapshot.queryParams.clone === 'true';
    this.campaignId = this.isAdding ? undefined : this.route.snapshot.params.campaignId;
    this.advertiserId = this.route.snapshot.params.advertiserId;
    this.originalCampaignId = this.campaignId;
  }

  async ngOnInit() {
    try {
      if (this.isCloning) {
        await this.cloneCampaign();
      } else {
        this.campaign = this.campaignId
          ? await lastValueFrom(this.campaignsApi.getCampaign(this.campaignId))
          : new Campaign({ advertiserId: this.advertiserId });
      }
    } catch (err: any) {}

    // Load location filters - following punch-card pattern
    if (this.isCloning) {
      // When cloning, location filters are already loaded in cloneCampaign()
      this.campaignLocationFilters().setFormValue(this.clonedLocationFilters);
    } else {
      const clf = this.campaignId
        ? await lastValueFrom(this.campaignLocationFiltersApi.getByCampaignId(this.campaignId))
        : new CampaignLocationFilter({ campaignId: this.campaignId, filters: [] });
      this.campaignLocationFilters().setFormValue(clf);
    }

    this.campaignReplay.next(this.campaign);

    setTimeout(() => {
      this.syncBanners();
    }, 500);
  }

  syncBanners() {
    // sync baseCampaign main banner with banner slider tab (can be edited from 2 places)
    this.campaignBase().mainBannerChanged.subscribe(banner => { this.campaignBanner().setMainBanner(banner); });
    this.campaignBanner().mainBannerChanged.subscribe(banner => { this.campaignBase().setMainBanner(banner); });

    this.campaignBanner().setBanners(this.campaign.strategy?.banner);

    if (!this.campaign.strategy?.banner) {
      // old campaings have banner set on campaign.photoBannerUrl prop -> add it to the banner list
      if (this.campaign.photoBannerUrl)
        this.campaignBanner().setMainBanner(new BannerImage({ photoUrl: this.campaign.photoBannerUrl }));
    }
    else {
      // new campaign will have banner list -> set it also on the campaign base main banner
      const strategyBanner = this.campaign.strategy.banner;
      if (strategyBanner && strategyBanner.url1)
        this.campaignBase().setMainBanner(new BannerImage({ photoUrl: strategyBanner.url1 }))
    }
  }

  private async cloneCampaign() {
    const { campaign, locationFilters } = await this.campaignCloneService.cloneCampaign(this.originalCampaignId, this.advertiserId);

    this.campaign = campaign;
    this.isAdding = true;
    this.campaignId = undefined;
    // Store location filters to be set in setTimeout when viewChild is ready
    this.clonedLocationFilters = locationFilters;
  }

  forceRefresh() {
    const c = this.campaign;
    this.campaign = null;
    setTimeout(() => {
      this.campaign = c;
    }, 500);

    this.syncBanners();
  }


  async onSave() {
    this.campaignOutboundEvents()?.markAsTouched();

    const request = this.campaignBase().getFormValue();
    const strategy = this.campaignStrategy().getFormValue();

    if (!request || !this.campaignStrategy().isValid() || !this.campaignOutboundEvents().isValid()) return;

    // Build request object
    Object.assign(request, {
      campaignId: this.campaignId,
      advertiserId: this.advertiserId,
      strategy: {
        ...strategy,
        title: request.title,
        allowDuplicates: request.allowDuplicates,
        banner: this.campaignBanner().getBanners()
      } as LuckyDrawStrategy,
      campaignLocationFilter: this.campaignLocationFilters().getFormValue()
    });

    try {
      const savedCampaign = await lastValueFrom(this.campaignsApi.createCampaign(request));

      const outboundEventsConfig = this.campaignOutboundEvents()?.getFormValue();
      if (outboundEventsConfig && savedCampaign?.campaignId) {
        try {
          await lastValueFrom(this.outboundEventsApi.updateCampaignConfig(savedCampaign.campaignId, outboundEventsConfig));
        } catch (err) {
          console.warn('Failed to save outbound events configuration:', err);
        }
      }

      this.snackBar.open(`Campaign ${request.title} was saved`, null, { duration: 3000 });
      this.router.navigate(['advertisers', this.advertiserId]);
    } catch (err: any) {
      console.error(err);
      if (err.status === 400 && err.response) {
        new MessageDialogModel(err.response).show(this.dialog);
      }
    }
  }

  refAssignObj(ref: any, obj: any) {
    if (!ref) return;
    for (const key of Object.keys(ref))
      delete ref[key];

    if (!obj) return;
    for (const key of Object.keys(obj))
      ref[key] = obj[key];
  }


}
